/**
 * SE2 · Kit cart path, shared (2026-11-04, batch 4).
 *
 * WHY THIS FILE. The free first bottle is a cart rule, not a page rule: on the Basic plan there is no Function and no
 * Cart Transform, so a kit is separate cart lines (_se2_kit / _se2_kit_role / _se2_kit_gift properties) plus the
 * offer code applied through /cart/update.js and checked in the cart that comes back. Build your kit has done that
 * since 2026-09-29 (assets/se2-kit.js). The diffuser page's "Add Plug + Santal" (assets/se2-dpdp-kit.js) must add
 * the same two lines the same way, so the sequence moved here, word for word, and both pages call it. One path means
 * one set of acceptance tests and one place where the code, the restore of the shopper's own codes and the
 * "did the bottle reach $0" check can go wrong.
 *
 * What stays in each page: what to add (the kit's lines, or the PDP's diffuser + first scent), the words, and what
 * to do with the outcome (the kit's notice panel, the PDP's line under its button).
 *
 * Exposes window.SE2KitCart. Loaded deferred, before the page script that uses it.
 *
 * TWO TIERS (owner, 2026-11-04): the SCENTPRO Mini's free bottle is a 50 mL with its own code, every other SCENTPRO's a
 * 120 mL (snippets/se2-kit-offer.liquid). The caller always chooses the code; this file never does. It takes the tier
 * ('std' | 'small') so a refused 50 mL code does not end the 120 mL offer for the session (or the reverse), and
 * kitCodes (both bottle codes) so a second kit's code reads "one per order", never "does not combine with" the store's
 * own other kit code.
 */
(() => {
  'use strict';

  if (window.SE2KitCart) return;

  const jsUrl = (u) => (/\.js$/.test(u) ? u : u + '.js');
  const upper = (code) => String(code || '').trim().toUpperCase();

  // 2026-11-04: the kit's shipping code (Settings > SE2 · Kit offer > Kit shipping code), printed by layout/theme.liquid
  // as <meta name="se2-kit-ship-code">. Shopify's automatic "Free shipping on subscription orders" does not cover a cart
  // that also holds a one-time diffuser, so a kit was charged shipping now and on every renewal. This free-shipping code
  // (first order and renewals, combines with the bottle code) goes on with the bottle code and stays on even when the
  // bottle does not apply, so a kit always ships free as the store promises.
  const shipCode = () => {
    const m = document.querySelector('meta[name="se2-kit-ship-code"]');
    return (m && m.getAttribute('content') || '').trim();
  };

  const session = {
    get(key) {
      try {
        return window.sessionStorage.getItem(key);
      } catch (e) {
        return null;
      }
    },
    set(key, value) {
      try {
        window.sessionStorage.setItem(key, value);
      } catch (e) {
        /* storage blocked: the page still works, it just forgets */
      }
    },
  };

  const dedupe = (codes) => {
    const seen = new Set();
    return codes.filter((code) => {
      const key = String(code || '').trim().toUpperCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  /** Any automatic discount on a cart, at cart level or on a line. */
  const hasAutomatic = (cart) =>
    Boolean(cart) &&
    ((cart.cart_level_discount_applications || []).some((a) => a.type === 'automatic') ||
      (cart.items || []).some((i) =>
        (i.line_level_discount_allocations || []).some((x) => x.discount_application && x.discount_application.type === 'automatic')
      ));

  function withDelimiters(cents, precision, thousands, decimal) {
    const negative = cents < 0;
    const abs = Math.abs(Math.round(cents));
    let out;
    if (precision === 0) {
      out = String(Math.round(abs / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    } else {
      out =
        String(Math.floor(abs / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, thousands) +
        decimal +
        String(abs % 100).padStart(2, '0');
    }
    return (negative ? '-' : '') + out;
  }

  /** Shopify's money format, from integer cents. Never a typed price. */
  function money(cents, format) {
    const pattern = /\{\{\s*(\w+)\s*\}\}/;
    const match = format ? format.match(pattern) : null;
    if (!match) return '$' + withDelimiters(cents, 2, ',', '.');
    let value;
    switch (match[1]) {
      case 'amount_no_decimals':
        value = withDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = withDelimiters(cents, 2, "'", '.');
        break;
      case 'amount_with_space_separator':
        value = withDelimiters(cents, 2, ' ', ',');
        break;
      default:
        value = withDelimiters(cents, 2, ',', '.');
    }
    return format.replace(pattern, value);
  }

  async function readResponse(response) {
    let body = null;
    try {
      body = await response.json();
    } catch (e) {
      body = null;
    }
    return { ok: Boolean(response.ok && body && typeof body === 'object' && !body.status), status: response.status, body };
  }

  async function postJson(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    return readResponse(response);
  }

  async function getCart(url) {
    const response = await fetch(url, { cache: 'no-store', credentials: 'same-origin', headers: { Accept: 'application/json' } });
    const result = await readResponse(response);
    if (!result.ok) throw new Error('cart ' + response.status);
    return result.body;
  }

  /** The theme's own routes, with the page's island as the fallback. */
  function routes(fallback) {
    const r = (window.Theme && window.Theme.routes) || {};
    const f = fallback || {};
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return {
      add: jsUrl(r.cart_add_url || f.cartAdd || root + 'cart/add'),
      update: jsUrl(r.cart_update_url || f.cartUpdate || root + 'cart/update'),
      change: jsUrl(r.cart_change_url || f.cartChange || root + 'cart/change'),
      cart: jsUrl(r.cart_url || f.cart || root + 'cart'),
      cartPage: r.cart_url || f.cart || root + 'cart',
    };
  }

  /** The cart sections on the page, re-rendered by the same request that adds. */
  function sections() {
    return Array.from(document.querySelectorAll('cart-items-component[data-section-id]')).map((e) => e.dataset.sectionId);
  }

  const newKitId = () => 'k' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);

  /** The code discounted at least one line, and none of those lines is on a selling plan. */
  function codeOffPlan(cart, code) {
    const want = upper(code);
    const lines = (cart.items || []).filter(
      (i) =>
        (i.line_level_discount_allocations || []).some((x) => {
          const app = x.discount_application || {};
          return Number(x.amount) > 0 && (upper(app.key) === want || upper(app.title) === want);
        }) || (i.discounts || []).some((d) => Number(d.amount) > 0 && upper(d.title) === want)
    );
    return lines.length > 0 && !lines.some((i) => i.selling_plan_allocation);
  }

  async function errorOutcome(r, kitId) {
    let cartJson = null;
    try {
      cartJson = await getCart(r.cart);
    } catch (e) {
      cartJson = null;
    }
    const giftLine = cartJson
      ? (cartJson.items || []).find((i) => i.properties && i.properties._se2_kit === kitId && i.properties._se2_kit_gift) || null
      : null;
    return { state: 'error', cartJson, sectionsHtml: undefined, giftLine };
  }

  /** A kit with no free bottle: put the shipping code on beside the shopper's own codes, and read the cart back. */
  async function shipOutcome(r, addBody, existing, secs) {
    const ship = shipCode();
    try {
      const res = await postJson(r.update, { discount: dedupe((existing || []).concat([ship])).join(','), sections: secs, sections_url: window.location.pathname });
      if (!res.ok) throw new Error('update');
      return { state: 'none', cartJson: res.body, sectionsHtml: res.body && res.body.sections };
    } catch (e) {
      return plainOutcome(r, addBody);
    }
  }

  async function plainOutcome(r, addBody) {
    let cartJson = null;
    try {
      cartJson = await getCart(r.cart);
    } catch (e) {
      cartJson = null;
    }
    return { state: 'none', cartJson, sectionsHtml: addBody && addBody.sections };
  }

  /**
   * Applies the code and reads the cart back: applied | one_per_order | not_applicable | conflict | error.
   *
   * existing: the codes on the cart before (always restored when the code does not apply, so the shopper never
   * loses a code). only: send the offer code alone (the kit notice's "Use the free first bottle"). autoBefore: an
   * automatic discount was on the cart before the kit went in. The outcome's memo is true only when the code does
   * not apply and nothing else explains it; the session then stops calling bottles free (se2-kit:offer), on every
   * page that reads that key.
   */
  async function applyOffer({ routes: r, kitId, code, existing, sections: secs, restore, only, autoBefore, tier, kitCodes }) {
    const ship = shipCode();
    const discount = only ? dedupe([code, ship]).join(',') : dedupe((existing || []).concat([code, ship])).join(',');
    let data;
    try {
      const res = await postJson(r.update, { discount, sections: secs, sections_url: window.location.pathname });
      if (!res.ok) throw new Error('update');
      data = res.body;
    } catch (e) {
      return errorOutcome(r, kitId);
    }
    const codes = data.discount_codes || [];
    const hit = codes.find((d) => upper(d.code) === upper(code));
    const kitLines = (data.items || []).filter((i) => i.properties && i.properties._se2_kit === kitId);
    const giftLine = kitLines.find((i) => i.properties._se2_kit_gift) || null;
    const autoNow = hasAutomatic(data);
    if (hit && hit.applicable === true) {
      if (!giftLine) return { state: 'error', cartJson: data, sectionsHtml: data.sections, giftLine: null };
      if (giftLine.final_line_price === 0) return { state: 'applied', cartJson: data, sectionsHtml: data.sections, giftLine };
      // The code gets one bottle of the offer size on a plan. With two such subscribed bottles in the kit, Shopify may
      // discount the extra instead: the order is still right, so this is applied too.
      const freeLine = kitLines.find((i) => i.selling_plan_allocation && Number(i.final_line_price) === 0);
      if (freeLine) return { state: 'applied', cartJson: data, sectionsHtml: data.sections, giftLine: freeLine };
      // The offer is a bottle on Subscribe & save (OD2). A code that discounted only lines with no plan (a one-time
      // bottle) did not give it: that is "not applicable", so the code comes off again (QA 2026-10-06, S-kit-04).
      if (!codeOffPlan(data, code)) return { state: 'one_per_order', cartJson: data, sectionsHtml: data.sections, giftLine };
    }
    // Not applicable: take the dead code off again and keep every code the shopper already had.
    // The shipping code stays: the kit ships free whether or not the bottle applies.
    const keepCodes = dedupe((existing || []).concat([ship]));
    let restored = data;
    if (restore !== false) {
      try {
        const res = await postJson(r.update, { discount: keepCodes.join(','), sections: secs, sections_url: window.location.pathname });
        if (!res.ok) throw new Error('restore');
        restored = res.body;
      } catch (e) {
        return errorOutcome(r, kitId);
      }
    }
    const restoredGift =
      (restored.items || []).find((i) => i.properties && i.properties._se2_kit === kitId && i.properties._se2_kit_gift) || giftLine;
    // Only a code that actually applies can be what the free bottle does not combine with.
    const kit = new Set((kitCodes || []).concat([code]).map(upper).filter(Boolean));
    const liveCodes = only ? [] : keepCodes.filter((c) => upper(c) !== upper(ship) && !kit.has(upper(c)) && codes.some((d) => upper(d.code) === upper(c) && d.applicable === true));
    const base = { cartJson: restored, sectionsHtml: restored.sections, giftLine: restoredGift };
    if (liveCodes.length) return Object.assign(base, { state: 'conflict', conflictCode: liveCodes[0] });
    // Another kit's bottle code is already on the cart: one free bottle per order, whichever size it is.
    const otherKit = keepCodes.some((c) => kit.has(upper(c)) && upper(c) !== upper(code) && codes.some((d) => upper(d.code) === upper(c) && d.applicable === true));
    if (otherKit) return Object.assign(base, { state: 'one_per_order' });
    if (autoBefore) return Object.assign(base, { state: 'conflict', conflictCode: null });
    // An automatic discount the kit's own lines triggered: the bottle did not apply to this order, but the code may
    // apply to another kit, so the session keeps the offer.
    const memo = !autoNow;
    if (memo) session.set(memoKey(tier), JSON.stringify({ state: 'unavailable', code, tier: tierOf(tier), at: Date.now() }));
    return Object.assign(base, { state: 'not_applicable', memo });
  }

  /**
   * The add sequence (spec C2.10, D.8): read the shopper's codes and automatic discounts first when a gift line is
   * in the items, POST the lines, then the outcome. items are /cart/add.js items with their _se2_kit properties
   * already set; gift is true when one of them carries _se2_kit_gift.
   * Returns { ok: false, description } when the add itself failed, else { ok: true, outcome, autoBefore }.
   */
  async function addKit({ items, gift, code, kitId, routes: r, sections: secs, tier, kitCodes }) {
    const rr = r || routes();
    const ss = secs || sections();
    let existing = [];
    let autoBefore = false;
    // A failed read means the codes on the cart are unknown: no code is written at all (writing the offer code alone
    // would replace the shopper's codes, and its restore would clear them; QA 2026-10-06, S-kit-05).
    let readFailed = false;
    // The shipping code goes on with every kit, with or without a free bottle (a Mini kit whose scent has no 50 mL
    // in stock still ships free, as the cart's "Ships free" tag says), so the codes are read first either way.
    const ship = shipCode();
    if (gift || ship) {
      try {
        const cart = await getCart(rr.cart);
        existing = (cart.discount_codes || []).map((d) => d.code).filter(Boolean);
        // A bottle code an earlier kit put on the cart is a kit code too, even if this page no longer prints it.
        const giftCodes = (cart.items || []).map((i) => i.properties && i.properties._se2_kit_gift).filter((c) => typeof c === 'string' && c);
        kitCodes = dedupe((kitCodes || []).concat(giftCodes));
        autoBefore = hasAutomatic(cart);
      } catch (e) {
        existing = [];
        readFailed = true;
      }
    }
    let added;
    try {
      added = await postJson(rr.add, { items, sections: ss, sections_url: window.location.pathname });
    } catch (e) {
      added = { ok: false, body: null };
    }
    if (!added.ok) {
      const description = (added.body && (added.body.description || added.body.message)) || '';
      return { ok: false, description, autoBefore };
    }
    let outcome;
    if (!gift && ship && !readFailed) outcome = await shipOutcome(rr, added.body, existing, ss);
    else if (!gift) outcome = await plainOutcome(rr, added.body);
    else if (readFailed) {
      outcome = await errorOutcome(rr, kitId);
      if (outcome.cartJson) outcome.sectionsHtml = added.body && added.body.sections;
    } else outcome = await applyOffer({ routes: rr, kitId, code, existing, sections: ss, restore: true, autoBefore, tier, kitCodes });
    return { ok: true, outcome, autoBefore };
  }

  /** Tells the theme's cart (drawer, bubble) through Horizon's own event, so it re-renders from the returned cart. */
  async function dispatchCart(source, outcome, items, action) {
    if (!outcome || !outcome.cartJson) return false;
    try {
      const { CartLinesUpdateEvent } = await import('@shopify/events');
      const deferred = CartLinesUpdateEvent.createPromise();
      (source || document).dispatchEvent(
        new CartLinesUpdateEvent({
          action,
          context: 'product',
          lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: 1 })),
          promise: deferred.promise,
        })
      );
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(outcome.cartJson),
        detail: {
          sections: outcome.sectionsHtml || undefined,
          items: outcome.cartJson.items,
          itemCount: outcome.cartJson.item_count,
          source: 'se2-kit',
          didError: false,
        },
      });
      return true;
    } catch (e) {
      return false;
    }
  }

  /** 'small' for the Mini's 50 mL tier, 'std' for everything else (and for a memo written before the tiers). */
  const tierOf = (tier) => (tier === 'small' ? 'small' : 'std');
  const memoKey = (tier) => (tierOf(tier) === 'small' ? 'se2-kit:offer:small' : 'se2-kit:offer');

  /** True while this session has seen the cart refuse that tier's code (the kit stops calling those bottles free). */
  function offerSpent(tier) {
    try {
      const raw = session.get(memoKey(tier));
      const memo = raw ? JSON.parse(raw) : null;
      return Boolean(memo && memo.state === 'unavailable');
    } catch (e) {
      return false;
    }
  }

  window.SE2KitCart = {
    jsUrl,
    upper,
    dedupe,
    hasAutomatic,
    money,
    readResponse,
    postJson,
    getCart,
    routes,
    sections,
    newKitId,
    codeOffPlan,
    errorOutcome,
    plainOutcome,
    shipOutcome,
    applyOffer,
    addKit,
    dispatchCart,
    offerSpent,
  };
})();
